import Drawer from '@base/Drawer/DrawerWithFooter';
import H3 from '@base/H3/H3';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import RadioButton from '@base/RadioButton/RadioButton';
import H5 from '@base/H5/H5';
import Dropdown from '@comp/Dropdown/Dropdown';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Button from '@base/Button/Button';
import P2 from '@base/P2/P2';
import { useEffect, useState } from 'react';
import Image from '@base/Image';
import pushRoute from '@utils/pushRoute';
import {
  DOW2_DOUBT_SUBMITTED,
  DOW2_DOUBT_SUBMIT_REQUESTED,
  DOW2_SUBJECT_SELECTED
} from '@constants/segment';
import { getAnimationPath } from '@utils/animationHelpers';

import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { captureException } from '@utils/sentry';
import { DoubtSubmitError } from '@utils/customErrors';
import useCustomDoubtsStore from '@stores/customDoubtsStore';
import {
  getInstantMatchFlagSetter,
  getSubjectSelectionFlowSetter
} from '@stores/selectors/customDoubtSelectors';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';

import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import useSubjectAndLanguages from '@api/hooks/goal/custom-doubts/useSubjectAndLanguages';
import useDoubtConfig from '@api/hooks/goal/custom-doubts/useDoubtConfig';
import { submitDoubt } from '@api/fetchers/doubts-and-solutions';
import { subjectSelectionState } from '../DoubtSolution/constants';
import defaultExtraProps from '../utils/defaultExecptionValues';

const AnimationContainer = styled(LottieAnimation)`
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
`;

const AnimationFallBackImage = ({ imageURL }) => {
  return <Image src={imageURL} width={80} height={80} alt="doubt-state-card" />;
};

const StyH3 = styled(H3)`
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_3B};
`;

const ImageWrapper = styled.div`
  margin-top: ${Spacings.SPACING_8B};
  width: 464px;
  height: 262.93px;
  position: relative;
  display: block;
  overflow: hidden;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    width: 360px;
    height: 203px;
  }
`;

const DountImage = styled.img`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const BackgroundWrapper = styled.div`
  width: 240px;
  height: 136px;
  background-image: url('${(props) => props.bgImageSrc}');
  display: block;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  z-index: 1;
  filter: blur(64px);
  top: 0;
  left: 0;
`;

const StyH5Info = styled(H5)`
  grid-column: span 10;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_5B};
`;

const DoubtImageWrapper = ({ content }) => {
  return (
    <ImageWrapper>
      <BackgroundWrapper bgImageSrc={content} />
      <DountImage src={content} alt="doubt-image" />
    </ImageWrapper>
  );
};

const StyDropDown = styled(Dropdown)`
  margin-bottom: ${Spacings.SPACING_40B};
  min-width: 224px;
`;

const PreferenceWrapper = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  border: ${({ isSelected }) => {
    if (isSelected) {
      return '1px solid var(--color-text-primary)';
    }
    return '1px solid var(--color-divider)';
  }};
  background: var(--color-base-1);
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_3B};
  grid-column: span 10;
  cursor: pointer;
  color: var(--color-text-primary);
  height: 56px;
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.SMALL};
`;

const StyRadio = styled(RadioButton)`
  &.MuiFormControlLabel-root {
    .MuiTypography-body1 {
      font-size: ${Fonts.SMALL};
      font-weight: ${Fonts.SEMIBOLD};
    }
  }
`;

const StyH5 = styled(H5)`
  grid-column: span 10;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_8B};
`;

const renderTolplogies = (
  filteredTopologies,
  selectedDoubtTopology,
  onChangeTopology
) => {
  const renderTopology = filteredTopologies.map((topology) => {
    const isSelected = topology.uid === selectedDoubtTopology.uid;
    return (
      <PreferenceWrapper
        key={topology.uid}
        onClick={() => onChangeTopology(topology.uid)}
        isSelected={isSelected}
      >
        <StyRadio
          checked={isSelected}
          label={topology.name}
          key={topology.uid}
          isSelected={isSelected}
          size="small"
        />
      </PreferenceWrapper>
    );
  });
  return (
    <>
      <StyH5>Confirm subject</StyH5>
      {renderTopology}
    </>
  );
};

const FooterWrapper = styled.div`
  grid-column: span 10;
  height: 72px;
  background: var(--color-base-1);
  display: flex;
  justify-content: space-between;
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_10B};
  align-items: center;
  box-shadow: ${boxShadowGenerator({ yOffset: '-16px', blur: '32px' })};
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    height: 116px;
    padding: ${Spacings.SPACING_4B};
  }
`;

const StyButton = styled(Button)`
  margin-right: ${Spacings.SPACING_19B};
  color: var(--color-base-1);
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-right: ${Spacings.SPACING_0B};
  }
`;

const ErrorDiv = styled.div`
  display: flex;
  align-items: center;
`;
const ErrorMsg = styled(P2)`
  color: var(--color-i-red);
  margin-left: ${Spacings.SPACING_2B};
  width: 272px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const ErrorImage = styled(Image)``;

const ContentWrapper = styled.div`
  grid-column: span 10;
`;

const Footer = ({ errorMsg, handleDoubtSubmit, footerButtonState }) => {
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const loadingStateAnim = (
    <AnimationContainer
      Fallback={<AnimationFallBackImage imageURL="doubt20/expandIconDM.svg" />}
      src={getAnimationPath(
        isDarkModeOn
          ? 'doubtsOnWeb/3-dot-loader-Dark-mode.json'
          : 'doubtsOnWeb/3-dot-loader-Light-mode.json'
      )}
    />
  );
  return (
    <FooterWrapper>
      <ErrorDiv>
        {errorMsg ? (
          <ErrorImage
            src="oneToOneLiveMentoring/errorExclamation.svg"
            width={Spacings.SPACING_5B}
            height={Spacings.SPACING_5B}
            alt="error"
          />
        ) : null}
        <ErrorMsg>{errorMsg || ''}</ErrorMsg>
      </ErrorDiv>
      <StyButton
        label={footerButtonState === 'loading' ? loadingStateAnim : 'Submit'}
        variant={footerButtonState}
        color="black"
        width="136px"
        onClick={handleDoubtSubmit}
      />
    </FooterWrapper>
  );
};

const ConfirmDoubtDetails = ({
  goalUID,
  drawerState,
  setUploadedImageUrl,
  uploadedImageUrl
}) => {
  const [errorMsg, setError] = useState(null);
  const setInstantMatch = useCustomDoubtsStore(getInstantMatchFlagSetter);
  const updateSubjectSelectedState = useCustomDoubtsStore(
    getSubjectSelectionFlowSetter
  );
  const { subjectAndLanguageData: subjectsAndLanguages } =
    useSubjectAndLanguages(goalUID);
  const {
    doubtConfigData: { preferredLanguage }
  } = useDoubtConfig(goalUID);
  const { topologies, languages } = subjectsAndLanguages || {};
  const filteredTopologies =
    topologies && topologies.length > 1 ? topologies.slice(1) : [];
  const filteredLanguages =
    languages && languages.length > 1 ? languages.slice(1) : [];
  const [selectedDoubtTopology, setTopology] = useState(filteredTopologies[0]);
  const [selectedDoubtLanguage, setLanguage] = useState(preferredLanguage);
  const { goalInfo } = useGoalInfo(goalUID);

  const onChangeTopology = (topologyUID) => {
    const selectedTopology = filteredTopologies.find(
      (topology) => topology.uid === topologyUID
    );
    setTopology(selectedTopology);
  };
  const routeGoalSegmentData = useGoalEventProps({ goalUID });
  const onChangeLanaguage = (languageUID) => {
    setLanguage(languageUID);
  };
  let doubtUID = null;
  const myUserUID = useMyInfo(getMeUid);
  const [footerButtonState, setFooterButtonState] = useState('filled');

  const handleDoubtSubmit = async () => {
    setFooterButtonState('loading');
    setError('');
    const reqBody = {
      uploadedImageUrl,
      selectedDoubtLanguage,
      selectedDoubtTopology,
      isASDEnabled: false
    };
    const doubtResponse = await submitDoubt(goalUID, reqBody);
    if (doubtResponse.success) {
      doubtUID = doubtResponse.uid;
      updateSubjectSelectedState(
        goalUID,
        doubtUID,
        subjectSelectionState.regularFlow
      );
      pushRoute(
        {
          pathname:
            '/goal/[goalSlug]/[goalUID]/doubts-and-solutions/doubt/[doubtUID]',
          query: {
            goalSlug: goalInfo.slug,
            goalUID: goalInfo.uid,
            doubtUID
          }
        },
        undefined,
        { shallow: false }
      );
      segment.track(DOW2_DOUBT_SUBMITTED, {
        doubt_upload_status: 'Success',
        doubt_type: 'Ask A Doubt',
        doubtUID,
        feature_version: 2,
        flow_type: 'Non_Demo',
        doubt_subject_selection: subjectSelectionState.regularFlow,
        ...routeGoalSegmentData
      });
      segment.track(DOW2_SUBJECT_SELECTED, {
        doubt_upload_status: 'Success',
        doubt_type: 'Ask A Doubt',
        doubt_id: doubtUID,
        feature_version: 2,
        flow_type: 'Non_Demo',
        doubt_subject_selection: subjectSelectionState.regularFlow,
        subject: selectedDoubtTopology?.name,
        ...routeGoalSegmentData
      });
      setInstantMatch(true);
    } else {
      segment.track(DOW2_DOUBT_SUBMITTED, {
        doubt_upload_status: 'Failed',
        doubt_type: 'Ask A Doubt',
        feature_version: 2,
        flow_type: 'Non_Demo',
        doubt_subject_selection: subjectSelectionState.regularFlow,
        ...routeGoalSegmentData
      });
      const defaultExtraPropsObj = defaultExtraProps(myUserUID, doubtResponse);
      captureException(
        new DoubtSubmitError('Doubt Submit Failed', doubtResponse),
        null,
        null,
        defaultExtraPropsObj
      );
      setError('Something went wrong. Please try again');
    }
  };

  const onCloseDrawer = () => {
    if (footerButtonState === 'loading') return null;
    setUploadedImageUrl(null);
  };

  useEffect(() => {
    segment.track(DOW2_DOUBT_SUBMIT_REQUESTED, {
      retake_image: 'No',
      submission_medium: 'Upload',
      doubt_type: 'Ask A Doubt',
      flow_type: 'Non_Demo',
      doubt_subject_selection: 'Regular AAD flow',
      ...routeGoalSegmentData
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Drawer
      disablePointerOnClose={footerButtonState === 'loading'}
      drawerState={drawerState}
      footer={
        <Footer
          errorMsg={errorMsg}
          handleDoubtSubmit={handleDoubtSubmit}
          footerButtonState={footerButtonState}
          setFooterButtonState={setFooterButtonState}
        />
      }
      onClose={onCloseDrawer}
    >
      <ContentWrapper>
        <StyH3>Submit doubt</StyH3>
        <DoubtImageWrapper content={uploadedImageUrl} />
        {renderTolplogies(
          filteredTopologies,
          selectedDoubtTopology,
          onChangeTopology
        )}
        <StyH5Info>Preferred solution language</StyH5Info>
        <StyDropDown
          options={filteredLanguages}
          gridColumn="span 5"
          selectedOption={selectedDoubtLanguage}
          onOptionClick={(lang) => onChangeLanaguage(lang)}
          menuWidth="224px"
          showSelectedBorder={false}
        />
      </ContentWrapper>
    </Drawer>
  );
};

export default ConfirmDoubtDetails;
